import React from 'react';
import {
  Box,
  Typography,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Chip
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { COST_RATES } from '../../utils/costRates';

const StepFinishingLevel = ({
  data,
  onChange,
  onNext,
  onBack,
  language = 'en'
}) => {
  const content = {
    en: {
      stepLabel: 'Step 2 of 3',
      title: 'Finishing Level',
      subtitle: 'Select the quality of finishes for your home',
      recommended: 'Recommended',
      perSqft: '/sq ft',
      continueBtn: 'Continue',
      backBtn: 'Back'
    },
    my: {
      stepLabel: 'Langkah 2 daripada 3',
      title: 'Tahap Kemasan',
      subtitle: 'Pilih kualiti kemasan untuk rumah anda',
      recommended: 'Disyorkan',
      perSqft: '/kaki persegi',
      continueBtn: 'Teruskan',
      backBtn: 'Kembali'
    }
  };

  const t = content[language] || content.en;

  const handleChange = (event, newLevel) => {
    if (newLevel !== null) {
      onChange({ ...data, finishingLevel: newLevel });
    }
  };

  const isValid = data.finishingLevel;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto' }}>
      {/* Progress indicator */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t.stepLabel}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'primary.main' }} />
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'primary.main' }} />
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'action.disabledBackground' }} />
        </Box>
      </Box>

      {/* Title */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        {t.title}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        {t.subtitle}
      </Typography>

      {/* Finishing Level Selection */}
      <ToggleButtonGroup
        value={data.finishingLevel}
        exclusive
        onChange={handleChange}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
          '& .MuiToggleButton-root': {
            border: '2px solid',
            borderColor: 'divider',
            borderRadius: '16px !important',
            p: 0,
            overflow: 'hidden',
            textTransform: 'none',
            '&.Mui-selected': {
              borderColor: 'primary.main',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }
          }
        }}
      >
        {Object.entries(COST_RATES).map(([key, config]) => (
          <ToggleButton key={key} value={key}>
            <Box sx={{ width: '100%', textAlign: 'left' }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  pb: 1.5,
                  bgcolor: data.finishingLevel === key ? 'primary.light' : 'transparent'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {data.finishingLevel === key && (
                    <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  )}
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
                    {language === 'my' ? config.labelMy : config.label}
                  </Typography>
                  {config.recommended && (
                    <Chip
                      label={t.recommended}
                      size="small"
                      color="primary"
                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
                    />
                  )}
                </Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: 'primary.main',
                    bgcolor: 'background.paper',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1
                  }}
                >
                  RM {config.min} - {config.max} {t.perSqft}
                </Typography>
              </Box>

              {/* Description */}
              <Box sx={{ px: 2, pb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {language === 'my' ? config.descriptionMy : config.description}
                </Typography>
              </Box>

              {/* Features */}
              <Box sx={{ px: 2, pb: 2 }}>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    pl: 2.5,
                    '& li': {
                      mb: 0.5,
                      color: 'text.secondary',
                      fontSize: '0.875rem'
                    }
                  }}
                >
                  {(language === 'my' ? config.featuresMy : config.features).map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </Box>
              </Box>
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 5 }}>
        <Button
          variant="text"
          onClick={onBack}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: 'none' }}
        >
          {t.backBtn}
        </Button>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={!isValid}
          endIcon={<ArrowForwardIcon />}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600
          }}
        >
          {t.continueBtn}
        </Button>
      </Box>
    </Box>
  );
};

export default StepFinishingLevel;
